import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useTranslations } from '../../../../../shared/src/hooks/use-translations.hook';
import { SubcategoriesActionOption } from '../SubcategoriesActionOption';
import { verticalActionTypesEnum } from '../../../../../shared/src/enums/vertical-types.enum';
import { useDeviceTypeHook } from '../../../../../shared/src/hooks/use-device-type.hook';

export const SubcategoriesFilterActionProviders = ({
    selectedOptions,
    onOptionClick,
    currentSubcategory,
}) => {
    const { t } = useTranslations();
    const { isMobile } = useDeviceTypeHook();
    const providerOptions = [];

    providerOptions.push({
        value: 'all',
        label: t('filterall'),
        selected: selectedOptions.length === 0,
    });

    const { gamesById } = useSelector(state => state.casino);
    const { favoriteGames } = useSelector(state => state.favorites);
    const { areFavoritesEnabled } = useSelector(state => state.app);

    const gameIds = useMemo(() => {
        const currentSubcategoryAction = currentSubcategory.action;
        const hasFavoritesGames = favoriteGames.length;
        const isFavoritesCategory = areFavoritesEnabled
            && currentSubcategoryAction === verticalActionTypesEnum.FAVORITES_CATEGORY;

        return isFavoritesCategory && hasFavoritesGames
            ? favoriteGames
            : currentSubcategory.gameIds;
    }, [currentSubcategory, favoriteGames, areFavoritesEnabled]);

    const gameProviders = useMemo(() => {
        const allProviders = [];

        gameIds.forEach(gameId => {
            const game = gamesById?.[gameId];
            if (!game) {
                return;
            }

            const gameProvider = game?.externalGameSubProvider || game?.externalGameProvider;
            if (gameProvider) {
                allProviders.push(gameProvider);
            }
        });

        // Return uniq values only.
        return [...new Set(allProviders)].sort();
    }, [gamesById, gameIds]);

    gameProviders.forEach(gameProvider => {
        providerOptions.push({
            value: gameProvider,
            label: gameProvider,
            selected: selectedOptions.indexOf(gameProvider) !== -1,
        });
    });

    const clickHandler = option => {
        const optionValue = option.value;
        let updatedValues = [...selectedOptions];

        if (optionValue === 'all') {
            updatedValues = [];
        } else if (selectedOptions.includes(optionValue)) {
            const optionIndex = updatedValues.indexOf(optionValue);
            updatedValues.splice(optionIndex, 1);
        } else {
            updatedValues.push(optionValue);
        }

        onOptionClick(updatedValues);
    };

    const providersLength = gameProviders.length;
    const providerOptionsMiddle = Math.ceil(providerOptions.length / 2);
    const optionsClassName = classNames(
        'asg-subcategories__actions-options',
        {
            'asg-subcategories__actions-options--mobile-view': isMobile,
            'asg-subcategories__actions-options--very-few-items': providersLength <= 2,
            'asg-subcategories__actions-options--few-items': 3 <= providersLength && providersLength <= 5,
            'asg-subcategories__actions-options--moderate-items': 6 <= providersLength && providersLength <= 14,
            'asg-subcategories__actions-options--large-items': 15 <= providersLength,
        }
    );

    return <div>
        <p className="asg-subcategories__actions-title">{t('filterProviders')}</p>
        <ul className={optionsClassName} style={{ '--items-middle': providerOptionsMiddle }}>
            {providerOptions.map((providerOption, index) => <SubcategoriesActionOption
                key={`${index}-${providerOption.value}`}
                option={providerOption}
                clickHandler={clickHandler}
            />)}
        </ul>
    </div>;
};
